import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import Text from "connectui/component/display/Text";
import SvgIcon from "connectui/component/display/SvgIcon";
import FlexContainer from "connectui/component/layout/FlexContainer";
import FilterList from "../../inbox/panel/filterlist/FilterList";
import Container from "connectui/component/layout/Container";
import LightButtonBase from "connectui/component/input/button/base/LightButtonBase";
import ButtonText from "connectui/component/input/button/ButtonText";
import TextButtonBase from "connectui/component/input/button/base/TextButtonBase";
import SelectedFiltersOverview from "../../inbox/filterlist/SelectedFiltersOverview";
import {CrmContext} from "../../../../routes/CrmPage";
import ICONS from "connectui/icons/iconMap";
import ButtonIcon from "../../../../connectui/component/input/button/ButtonIcon";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      overflow: "hidden",
    },
    header: {
      width: "100%",
      marginTop: theme.spacing(4),
      padding: theme.spacing(0, 4),
    },
    icon: {
      width: "15px",
      height: "15px",
      cursor: "pointer",
    },
    actionsRow: {
      width: "100%",
      padding: theme.spacing(0, 4, 5),
    },
  });

const CrmFiltersPanel = ({
  onClose = () => null,
  updateFilters = () => null,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const {appliedFilters, setAppliedFilters, customFields, setCustomFields} =
    useContext(CrmContext);
  const [selectedFilters, setSelectedFilters] = useState(appliedFilters);
  const searchParams = new URLSearchParams(window.location.search);
  const viewId = searchParams.get("view_id");

  useEffect(() => {
    setSelectedFilters(appliedFilters);
  }, [appliedFilters]);

  const applyFilters = () => {
    if (viewId) {
      window.history.pushState({}, "", window.location.pathname);
    }

    // Here we don't change the URL directly - we update the filters which will update the URL
    setAppliedFilters(selectedFilters);
    updateFilters(selectedFilters);
    onClose();
  };

  const handleClearAll = () => {
    setSelectedFilters([]);
    setAppliedFilters([]);
    updateFilters([]);
    if (viewId) {
      window.history.pushState({}, "", window.location.pathname);
    }
    onClose();
  };

  return (
    <FlexContainer
      className={classes.container}
      flexDirection="column"
      gap={theme.spacing(4)}
    >
      <FlexContainer
        className={classes.header}
        gap={theme.spacing(3)}
        alignItems="center"
      >
        <Text variant="displayXS" weight="semibold" sx={{flex: 1}}>
          {t("inbox.filters-panel.header")}
        </Text>
        <ButtonText
          buttonBase={TextButtonBase}
          typographyVariant="textSM"
          weight="semibold"
          color="primary"
          label={t("inbox.filters-panel.clear-all")}
          onClick={handleClearAll}
        />
        <ButtonIcon
          icon={
            <SvgIcon
              component={ICONS["layout-sidebar-left"]}
              sx={{
                width: "28px",
                height: "28px",
                padding: "5px",
                color: theme.colors.textSecondary,
              }}
            />
          }
          sx={{
            borderRadius: theme.spacing(2),
            cursor: "pointer",
            padding: 0,
            border: "none",
            backgroundColor: "transparent",
            marginTop: "3px",
            "&:hover": {backgroundColor: theme.colors.hoverGray},
          }}
          onClick={() => {
            onClose();
          }}
        />
      </FlexContainer>
      <FilterList
        filters={selectedFilters}
        setFilters={setSelectedFilters}
        customFields={customFields}
        setCustomFields={setCustomFields}
      />
      <SelectedFiltersOverview
        filters={selectedFilters}
        setFilters={setSelectedFilters}
      />
      <Container className={classes.actionsRow}>
        <FlexContainer>
          <ButtonText
            sx={{flex: 1}}
            typographyVariant="textSM"
            weight="semibold"
            color="white"
            label={t("inbox.filters-panel.apply-filters")}
            onClick={applyFilters}
          />
        </FlexContainer>
      </Container>
    </FlexContainer>
  );
};

export default CrmFiltersPanel;
